import { cn } from '@/utils';
import { ContentDescription } from '../components/ContentDescription';
import { ContentImage } from '../components/ContentImage';
import { ContentSection } from '../components/ContentSection';
import { ContentTitle } from '../components/ContentTitle';
import { ISimulationPlanningCard, ISimulationPlanningCardContentBlock } from '@/types';
import { roleplayCardIntroStyles } from '../../../Cards/RoleplayCard/RoleplayCard.styles';
import React from 'react';

interface Props {
  card: ISimulationPlanningCard;
}

/**
 * CardContentRenderer
 *
 * Renders the body content for a single planning card.
 *
 * Cards can contain heterogeneous content blocks. Each block is handled by `type`:
 * - `singular`: one container with one or more stacked sections.
 * - `multiple`: each section gets its own container card-like block.
 *
 * Persona-specific sections are detected by image path and rendered with avatar layout
 * so text and image align with roleplay design requirements.
 */
export const CardContentRenderer: React.FC<Props> = ({ card }) => {
  const styles = roleplayCardIntroStyles;

  /**
   * Singular content path:
   * Render all sections in a single white panel, preserving the original order.
   */
  const renderSingularContent = (content: ISimulationPlanningCardContentBlock) => {
    return content?.sections?.map((section, subIndex: number) => (
      <React.Fragment key={`section-${subIndex}`}>
        <ContentTitle section={section} />
        <ContentDescription section={section} />
        <ContentImage section={section} />
        <ContentSection section={section} />
      </React.Fragment>
    ));
  };

  /**
   * Multiple content path:
   * Render each section in its own white panel. For persona rows, switch to a split
   * text + avatar composition and use persona image rendering mode.
   */
  const renderMultipleContent = (content: ISimulationPlanningCardContentBlock) => {
    return content?.sections?.map((section, subIndex: number) => {
      // Persona sections are identified by image source path convention.
      const SECTION_IS_PERSONA = section?.image?.src.includes('/personas/');

      return (
        <div
          className="rounded-md bg-neutral-white p-6"
          data-key={`section[${subIndex}]`}
          key={`section-${subIndex}`}
        >
          <div className={styles.content}>
            <div className={cn(styles.descriptionContainer, 'justify-start')}>
              {/* Persona rows use a side-by-side block to prioritize avatar context. */}
              {SECTION_IS_PERSONA ? (
                <div className={styles.content}>
                  <div className={cn(styles.descriptionContainer, 'justify-start')}>
                    <ContentTitle section={section} />
                    <ContentDescription section={section} />
                  </div>
                  {/* Guard image rendering to avoid empty avatar containers. */}
                  {section?.image && (
                    <div className={cn(styles.avatarContainer, 'w-[240px]')}>
                      <ContentImage imageType="persona" section={section} />
                    </div>
                  )}
                </div>
              ) : (
                /* Standard section layout uses title + description + inline image stack. */
                <>
                  <ContentTitle section={section} />
                  <ContentDescription section={section} />
                  <ContentImage section={section} />
                </>
              )}

              {/* Shared section details are rendered regardless of persona mode. */}
              <ContentSection section={section} />
            </div>
          </div>
        </div>
      );
    });
  };

  // ==================================
  // Render
  // ==================================
  return (
    <>
      {card.content?.map((content, index) => {
        // =================================
        // Defensive guard: skip nullish entries while preserving map shape.
        if (!content) return null;

        // =================================
        // Multiple mode: one wrapper per block, then one panel per section.
        if (content.type === 'multiple') {
          return (
            <div data-key={`content[${index}]`} key={`content-${index}`}>
              {renderMultipleContent(content)}
            </div>
          );
        }

        // =================================
        // Singular mode: one panel containing all sections for this block.
        if (content.type === 'singular') {
          return (
            <div
              className="rounded-md bg-neutral-white p-6"
              data-key={`content-${index}`}
              key={`content-${index}`}
            >
              <div className={styles.content}>
                <div className={cn(styles.descriptionContainer, 'justify-start')}>
                  {renderSingularContent(content)}
                </div>
              </div>
            </div>
          );
        }

        // Unknown content type: fail silently to keep rendering resilient.
        return null;
      })}
    </>
  );
};

CardContentRenderer.displayName = 'CardContentRenderer';
